import { Slot } from 'radix-ui';
import type { ComponentProps, MouseEvent } from 'react';

export interface ButtonProps extends ComponentProps<'button'> {
  /** Render the single child instead of a button, keeping this element's props. */
  asChild?: boolean;
  /** Blocks interaction and announces the control as busy. Renders no indicator. */
  loading?: boolean;
}

export function Button({
  asChild = false,
  loading = false,
  disabled = false,
  type = 'button',
  onClick,
  ...props
}: ButtonProps) {
  const inactive = disabled || loading;

  if (asChild) {
    // The child may be an anchor, which has neither `type` nor `disabled`, so
    // the state is carried by ARIA and the caller styles from data-disabled.
    // ARIA alone does not stop a link navigating, so the default action is
    // blocked here. Slot runs the child's own handler before this one.
    const activate = (event: MouseEvent<HTMLButtonElement>) => {
      if (inactive) {
        event.preventDefault();
        return;
      }

      onClick?.(event);
    };

    return (
      <Slot.Root
        aria-busy={loading || undefined}
        aria-disabled={inactive || undefined}
        data-disabled={inactive ? '' : undefined}
        onClick={activate}
        {...props}
      />
    );
  }

  return (
    <button
      type={type}
      disabled={inactive}
      onClick={onClick}
      aria-busy={loading || undefined}
      data-disabled={inactive ? '' : undefined}
      {...props}
    />
  );
}
